import { createRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Route as rootRoute } from './__root';
import { AuthShell } from '../components/AuthShell';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

async function requestMagicLink(email: string) {
  const res = await fetch('/api/console/auth/magic-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Something went wrong. Please try again.');
}

function SendIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="19"
      height="16"
      viewBox="0 0 19 16"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path d="M0 16V10L8 8L0 6V0L19 8L0 16Z" fill="currentColor" />
    </svg>
  );
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () => requestMagicLink(email),
    onSuccess: () => {
      navigate({ to: '/check-email', search: { email } });
    },
    onError: (error) => {
      toast.error((error as Error).message);
    },
  });

  return (
    <AuthShell>
      <div className="mb-6 flex justify-center">
        <div className="text-subtle flex h-12 w-48 items-center justify-center rounded bg-white/85 text-sm">
          Logo
        </div>
      </div>

      <h1 className="text-primary text-center text-3xl font-semibold">
        Log in
      </h1>
      <p className="text-muted mx-auto mt-2 max-w-xs text-center text-sm">
        Get a magic link sent to your email that will sign you in instantly
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="relative mt-10 border-b border-white/25 focus-within:border-[--color-accent]"
      >
        <label htmlFor="email" className="sr-only">
          Work email
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="text-primary placeholder:text-subtle w-full bg-transparent py-2 pr-10 text-sm focus:outline-none"
        />
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-label={mutation.isPending ? 'Sending…' : 'Send login link'}
          className="text-accent absolute top-1/2 right-0 -translate-y-1/2 p-1 transition-opacity hover:opacity-70 disabled:pointer-events-none disabled:opacity-40"
        >
          <SendIcon />
        </button>
      </form>
    </AuthShell>
  );
}
